import { Link, useSearchParams } from 'react-router-dom';
import { currentWorks } from '@/mocks/portfolio';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import SlotText from '@/components/feature/SlotText';
import ImageViewer, { isTextCover } from '@/components/feature/ImageViewer';

export default function CurrentWorks() {
  const { ref, revealed } = useScrollReveal();
  const [, setSearchParams] = useSearchParams();

  const focusTags = [
    { key: 'brand-cx', label: 'Brand / CX', ja: 'ブランド・CX' },
    { key: 'digital-service', label: 'Digital / Service', ja: 'デジタル・サービス' },
    { key: 'experience-space', label: 'Experience / Space', ja: '体験・空間' },
    { key: 'retail-communication', label: 'Retail / Comm', ja: 'リテール・コミュニケーション' },
  ];

  return (
    <section id="works" className="py-20 md:py-28 bg-background-100">
      <div className="mx-auto max-w-[1440px] px-4 md:px-6">
        {/* Header */}
        <div
          ref={ref}
          className={`mb-12 transition-all duration-1000 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
            <span className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
              Current Focus — Where I Create Value Now
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-6">
              <h2 className="font-heading fw-bold text-[clamp(32px,4.5vw,60px)] text-foreground-950 leading-[0.93] tracking-[-0.04em]">
                Selected
                <br />
                <SlotText text="Works." as="span" className="fw-medium marker-yellow" color="oklch(0.09 0.01 70)" scrambleColor="rainbow" />
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-foreground-700 text-base leading-relaxed">
                現在、Business Design / Brand Strategy / CX Planning / Communication Strategy / Service & Experience Designの領域で価値を提供しています。最新案件の羅列ではなく、今どの領域で価値を生み出しているかを示すセクションです。
              </p>
            </div>
          </div>
        </div>

        {/* Focus labels — clickable, filter the works list below */}
        <div
          className={`mb-8 flex flex-wrap gap-2 md:gap-3 transition-all duration-1000 delay-100 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {focusTags.map((tag) => (
            <button
              key={tag.key}
              onClick={() => setSearchParams({ cap: tag.key })}
              className="group text-left px-4 py-2 md:px-5 md:py-2.5 bg-background-200 text-foreground-700 border border-background-300 cursor-pointer hover:bg-primary-500 hover:border-primary-500 transition-colors"
            >
              <SlotText
                text={tag.label}
                as="span"
                className="font-label text-[11px] md:text-xs tracking-[0.12em] uppercase text-foreground-700 group-hover:text-foreground-950 transition-colors"
              />
              <span className="block font-label text-[9px] tracking-[0.08em] uppercase text-foreground-400 mt-0.5 group-hover:text-foreground-700 transition-colors">
                {tag.ja} · 作品を見る
              </span>
            </button>
          ))}
        </div>

        {/* Work grid */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 transition-all duration-1000 delay-200 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {currentWorks.map((w, i) => (
            <Link
              key={w.id}
              to={`/case/${w.id}`}
              className={`case-card group block ${i === 0 ? 'md:col-span-2' : ''}`}
            >
              <div className="relative overflow-hidden bg-background-50">
                <div className={`w-full ${i === 0 ? 'aspect-[4/3] md:aspect-[16/9]' : 'aspect-[4/3]'}`}>
                  {i === 0 ? (
                    <>
                      <img
                        src={(w as { mobileImage?: string }).mobileImage || w.image}
                        alt={w.title}
                        className="case-img w-full h-full object-contain object-center bg-background-50 md:hidden"
                      />
                      <img
                        src={w.image}
                        alt={w.title}
                        className="case-img w-full h-full object-contain object-center bg-background-50 hidden md:block"
                      />
                    </>
                  ) : (
                    <img
                      src={w.image}
                      alt={w.title}
                      className="case-img w-full h-full object-contain object-center bg-background-50"
                    />
                  )}
                </div>
                <div className="flow-glow absolute inset-0" />
                <div className={`${i === 0 ? 'hidden md:block' : ''} absolute inset-0 z-10`}>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
                  <div className={`absolute bottom-0 left-0 right-0 z-20 ${i === 0 ? 'p-6 md:p-10' : 'p-4 md:p-6'}`}>
                    <p className="font-label text-[10px] tracking-[0.12em] uppercase text-white/50 mb-1">
                      {w.category}
                    </p>
                    <h3 className={`font-heading text-white tracking-[-0.02em] mb-3 ${i === 0 ? 'text-2xl md:text-3xl' : 'text-lg md:text-xl'}`}>
                      <SlotText text={w.title} scrambleColor="rainbow" />
                    </h3>
                    <div className={`grid grid-cols-1 gap-2 ${i === 0 ? 'md:grid-cols-3 gap-4' : ''}`}>
                      <div>
                        <p className="font-label text-[9px] tracking-[0.12em] uppercase text-white/40 mb-0.5">
                          Focus
                        </p>
                        <p className={`text-white/70 leading-relaxed ${i === 0 ? 'text-xs' : 'text-[10px]'}`}>{w.focus}</p>
                      </div>
                      {i === 0 && (
                        <div className="md:col-span-2">
                          <p className="font-label text-[9px] tracking-[0.12em] uppercase text-white/40 mb-0.5">
                            What
                          </p>
                          <p className="text-xs text-white/70 leading-relaxed line-clamp-2">{w.desc}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="absolute top-4 right-4 z-30 opacity-0 group-hover:opacity-100 transition-opacity">
                  <i className="ri-arrow-right-line text-white w-5 h-5 flex items-center justify-center" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}